import React from 'react';
import type { HeadingProps } from '@usewaypoint/block-heading';
import { convertEmailPillsToBrackets, convertVariablesToPlaceholders } from '../../../../../constants/variablePlaceholders';
import { convertEmailBuilderStylesToCSS } from '../Text/convertEmailBuilderStyles';

/**
 * Custom Heading component that renders {{variables}} as styled placeholders
 * in the email canvas preview
 *
 * NOTE: We bypass Waypoint's <Heading> component because it doesn't support
 * HTML rendering even with markdown: true. This component renders HTML directly
 * using dangerouslySetInnerHTML to properly display variable placeholders.
 */
export default function HeadingWithPlaceholders(props: HeadingProps) {
  // Get the text content and level
  const text = props.props?.text || '';
  const level = props.props?.level || 'h2';

  // Step 1: Convert HTML pill spans to {{variables}}
  const textWithBrackets = convertEmailPillsToBrackets(text);

  // Step 2: Convert {{variables}} to styled placeholder HTML
  const textWithPlaceholders = convertVariablesToPlaceholders(textWithBrackets);

  // Convert email-builder-js style format to React CSS
  const baseStyle = convertEmailBuilderStylesToCSS(props.style);

  // Add heading-specific styles
  const getFontSize = (headingLevel: string) => {
    switch (headingLevel) {
      case 'h1':
        return '32px';
      case 'h2':
        return '24px';
      case 'h3':
        return '20px';
      default:
        return '24px';
    }
  };

  const headingStyle = {
    ...baseStyle,
    fontSize: getFontSize(level),
    fontWeight: baseStyle.fontWeight || 'bold', // Default to bold if not specified
    margin: 0, // Match Waypoint's default
  };

  // Render the appropriate heading tag with HTML content
  const HeadingTag = level as 'h1' | 'h2' | 'h3';

  return (
    <HeadingTag
      style={headingStyle}
      dangerouslySetInnerHTML={{ __html: textWithPlaceholders }}
    />
  );
}
